import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../contexts/ThemeContext.tsx";
import { useAuth0 } from "@auth0/auth0-react";

const Nav = styled.nav`
  position: sticky;
  top: 0;
  width: 100%;
  background-color: #001f52;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.div`
  font-weight: bold;
  color: #64da4c;
  font-size: 1.5rem;
`;

const LinksWrapper = styled.div<{ open: boolean }>`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    flex-direction: column;
    background-color: #001f52;
    max-height: ${({ open }) => (open ? "400px" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease;
    box-shadow: ${({ open }) => (open ? "0 4px 6px rgba(0,0,0,0.3)" : "none")};
    gap: 0.5rem;
    padding-bottom: 0;
    border-radius: ${({ open }) => (open ? "0 0 8px 8px" : "0")};
  }
`;

const NavLink = styled(Link)`
  color: #f2f3f6;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: #64da4c;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 2rem;
    width: 100%;
    text-align: left;
    white-space: normal;
  }
`;

const Burger = styled.div<{ open: boolean }>`
  display: none;
  flex-direction: column;
  cursor: pointer;
  gap: 5px;
  z-index: 1100;

  span {
    height: 3px;
    width: 25px;
    background: white;
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  @media (max-width: 768px) {
    display: flex;
  }

  span:nth-child(1) {
    transform: ${({ open }) => (open ? "translateY(8px) rotate(45deg)" : "none")};
  }
  span:nth-child(2) {
    opacity: ${({ open }) => (open ? 0 : 1)};
  }
  span:nth-child(3) {
    transform: ${({ open }) => (open ? "translateY(-8px) rotate(-45deg)" : "none")};
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 2px solid #ff6b6b;
  color: #ff6b6b;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #ff6b6b;
    color: #001f52;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    margin-top: 0;
    width: 100%;
    justify-content: center;
  }
`;

const ThemeToggleButton = styled.button`
  background: transparent;
  border: 2px solid #64da4c;
  color: #64da4c;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #64da4c;
    color: #001f52;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    margin-top: 0;
    width: 100%;
    justify-content: center;
  }
`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { logout, isAuthenticated } = useAuth0();

  const handleClick = () => setOpen(!open);

  const closeMenu = () => setOpen(false);

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <Nav>
      <NavContainer>
        <Logo>Emanuel admin portal</Logo>

        <Burger open={open} onClick={handleClick}>
          <span />
          <span />
          <span />
        </Burger>

        <LinksWrapper open={open}>
          {isAuthenticated && (
            <>
              <NavLink to="/admins" onClick={closeMenu}>
                Admins
              </NavLink>
              <NavLink to="/conversations" onClick={closeMenu}>
                Samtaler
              </NavLink>
              <NavLink to="/config" onClick={closeMenu}>
                Kode
              </NavLink>
              <NavLink to="/categories" onClick={closeMenu}>
                Kategorier
              </NavLink>
              <LogoutButton onClick={handleLogout}>
                Log ud
              </LogoutButton>
            </>
          )}
          <ThemeToggleButton onClick={toggleTheme}>
            {theme === "dark" ? "☀️" : "🌙"} {theme === "dark" ? "Lys" : "Mørk"}
          </ThemeToggleButton>
        </LinksWrapper>
      </NavContainer>
    </Nav>
  );
}